import { CSSProperties } from "react";

import { Button, Classes } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { Link } from "react-router";

import { Box } from "@/components/layout/flexbox";
import { BP_NS, BRAND_PURPLE } from "@/css/constants";

const SegmentedButton = styled(Button)`
  border-radius: 10px;
  background-color: transparent;
  font-weight: normal;
  font-size: 16px;

  &.${Classes.BUTTON}.active:not([class*="${BP_NS}-intent- active"]),
  &.${Classes.BUTTON}.active:not([class*="${BP_NS}-intent- active"]):disabled {
    background-color: white;
    color: ${({
      theme,
    }: {
      theme: {
        colors: {
          activeColor: string;
        };
      };
    }) => theme.colors.activeColor};
    font-weight: 600;

    &:hover {
      background: white;
    }
  }

  &.${Classes.BUTTON}:not([class*='${BP_NS}-intent- active']):disabled,
  &.${Classes.BUTTON}:not([class*='${BP_NS}-intent-']):hover {
    background-color: transparent;
    box-shadow: none;
  }

  &.${Classes.BUTTON}:not([class*='${BP_NS}-intent- active']) {
    border-radius: 8px;
    background-color: transparent;
    font-weight: normal;
    border-width: 0;
    padding: 10px 20px;
    box-shadow: none;
    box-sizing: border-box;
  }

  @media only screen and (max-width: 767px) {
    &.${Classes.BUTTON}:not([class*='${BP_NS}-intent-']) {
      padding: 10px 20px;
    }
  }
`;

interface SegmentedProps<T> {
  loading?: boolean;
  value: T;
  options: { label: string; value: T; to?: string }[];
  disabled?: boolean;
  onChange: (value: T) => void;
  buttonStyle?: CSSProperties;
  activeColor?: string;
  containerStyle?: CSSProperties;
}

export function Segmented<T>({
  loading,
  value,
  options,
  disabled,
  onChange,
  buttonStyle,
  activeColor = BRAND_PURPLE,
  containerStyle,
}: SegmentedProps<T>) {
  if (loading) return null;

  const handleChange = (value: T) => {
    if (disabled) return;
    onChange(value);
  };

  return (
    <Box
      bg="#F0EFF8"
      css={{
        border: `3px solid #F0EFF8`,
        width: "fit-content",
        borderRadius: 10,
        ...containerStyle,
      }}
    >
      {options.map((option) => {
        const content = (
          <SegmentedButton
            style={buttonStyle}
            key={option.value + ""}
            className={option.value === value ? "active" : ""}
            onClick={() => handleChange(option.value)}
            disabled={disabled}
            theme={{ colors: { activeColor } }}
          >
            {option.label}
          </SegmentedButton>
        );
        return option?.to ? (
          <Link to={option.to} key={option.value + ""}>
            {content}
          </Link>
        ) : (
          content
        );
      })}
    </Box>
  );
}
